import { z } from "zod";
import { parseLang } from "../i18n/langs";

/** Where a newsletter signup came from. Only the footer field exists today. */
export const NEWSLETTER_SOURCES = ["footer"] as const;
export type NewsletterSource = (typeof NEWSLETTER_SOURCES)[number];

/**
 * The email as we store and compare it: trimmed and lowercased, so
 * "Sara@Example.com " and "sara@example.com" are one subscriber.
 */
export function normalizeNewsletterEmail(raw: string): string {
  return raw.trim().toLowerCase();
}

/** Same check on the web field and on the API, so the two never disagree. */
export function isValidNewsletterEmail(raw: string): boolean {
  return newsletterEmailSchema.safeParse(raw).success;
}

const newsletterEmailSchema = z
  .string()
  .transform(normalizeNewsletterEmail)
  .pipe(z.email().max(254));

export const newsletterSignupSchema = z.object({
  email: newsletterEmailSchema,
  /** Any value we do not publish falls back to English rather than failing. */
  lang: z.unknown().optional().transform((v) => parseLang(v)),
  source: z.enum(NEWSLETTER_SOURCES).default("footer"),
  /**
   * Honeypot. The web form renders this hidden from people; a bot that fills
   * every field fills it. Anything in it and the signup is quietly dropped.
   */
  company: z.string().max(200).optional(),
});

export type NewsletterSignupInput = z.input<typeof newsletterSignupSchema>;
export type NewsletterSignupParsed = z.output<typeof newsletterSignupSchema>;
